import swirlBottomRight from '@assets/images/selector-backgrounds/swirl-bottom-right.svg';
import swirlTopLeft from '@assets/images/selector-backgrounds/swirl-top-left.svg';
import { CobaltAvatar } from '@cae/cobalt-react/components/avatar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltSearchField } from '@cae/cobalt-react/components/search-field';
import { CobaltClickableTile } from '@cae/cobalt-react/components/tile';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconLogOutSolid } from '@cae/cobalt-react/icons/solid/CobaltIconLogOutSolid';
import { useOrganizations } from '@contexts/organizations-provider';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { getNameParts } from '@shared/helpers/get-name-parts/getNameParts';
import { useLogout } from '@shared/hooks/use-logout/useLogout';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './OrganizationSelectorPage.module.css';

const OrganizationSelectorPage = React.memo((): React.ReactElement => {
  const navigate = useNavigate();
  const { organizations, selectOrganization, isOrganizationsLoading } =
    useOrganizations();
  const [isSelecting, setIsSelecting] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const logout = useLogout();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const titleId = 'organization-selector-title';

  const showSearch = organizations.length >= 10;

  const filteredOrganizations = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return organizations;
    }
    const query = searchQuery.toLowerCase();
    return organizations.filter(org => org.name.toLowerCase().includes(query));
  }, [organizations, searchQuery]);

  const handleOrganizationSelect = React.useCallback(
    async (orgId: string) => {
      const org = organizations?.find(o => o.id === orgId);
      if (!org) return;

      setIsSelecting(true);
      try {
        await selectOrganization(org.id);
        navigate('/app-initializer', { replace: true });
      } catch (error) {
        console.error('Error selecting organization:', error);
        setIsSelecting(false);
      }
    },
    [organizations, selectOrganization, navigate]
  );

  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    []
  );

  const handleSearchClear = React.useCallback(() => {
    setSearchQuery('');
  }, []);

  if (isOrganizationsLoading || isSelecting) {
    return <AppLoader />;
  }

  return (
    <div
      className={styles.organizationSelectorPage}
      data-testid="organization-selector-page"
    >
      <img
        src={swirlTopLeft}
        alt=""
        aria-hidden="true"
        className={styles.swirlTopLeft}
      />
      <img
        src={swirlBottomRight}
        alt=""
        aria-hidden="true"
        className={styles.swirlBottomRight}
      />

      <div
        className={styles.organizationSelectorHeader}
        data-testid="organization-selector-header"
      >
        <CobaltButton variant="ghost" onClick={logout}>
          Log out
          <CobaltIconLogOutSolid />
        </CobaltButton>
      </div>

      <section
        className={styles.organizationSelectorContent}
        data-testid="organization-selector-content"
        aria-labelledby={titleId}
      >
        <div className={styles.organizationSelectorContentHeader}>
          {isMobile ? (
            <h1 className="heading-01">
              What{' '}
              <span className={styles.organizationSelectorHighlight}>
                organization
              </span>{' '}
              do you want
              <br />
              to sign in as?
            </h1>
          ) : (
            <>
              <h1 className="heading-04">
                What{' '}
                <span className={styles.organizationSelectorHighlight}>
                  organization
                </span>{' '}
                do you want to sign in as?
              </h1>
              <p className="body-02">
                You can switch between your organizations in the app anytime.
              </p>
            </>
          )}
        </div>

        <div
          className={styles.organizationsContainer}
          data-testid="organization-selector-list"
        >
          {showSearch && (
            <CobaltSearchField
              className={styles.organizationsSearch}
              placeholder="Search organizations"
              value={searchQuery}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              hasClear={Boolean(searchQuery)}
            />
          )}

          <div className={styles.organizationList}>
            {filteredOrganizations.length === 0 ? (
              <p className="body-02" data-testid="no-results-message">
                No organizations found
              </p>
            ) : (
              filteredOrganizations.map(org => {
                const { firstName, lastName } = getNameParts(org.name);
                return (
                  <CobaltClickableTile
                    id={org.id}
                    key={org.id}
                    onClick={() => handleOrganizationSelect(org.id)}
                  >
                    <div className={styles.organizationTileContent}>
                      <CobaltAvatar
                        color="neutral"
                        size="medium"
                        variant="initials"
                        firstName={firstName}
                        lastName={lastName}
                      />
                      <span className={`${styles.organizationName} body-02`}>
                        {org.name}
                      </span>
                    </div>
                  </CobaltClickableTile>
                );
              })
            )}
          </div>
        </div>
      </section>

      {isMobile && (
        <p className={`body-01 ${styles.organizationSelectorMobileFooter}`}>
          You can switch between your organizations in the app anytime.
        </p>
      )}
    </div>
  );
});

OrganizationSelectorPage.displayName = 'OrganizationSelectorPage';

export { OrganizationSelectorPage };
